import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';

// Custom hook for accessing Redux state and dispatch
export const useRedux = () => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  
  return { state, dispatch };
};

// Custom hook for auth state
export const useAuth = () => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  
  return { auth, dispatch };
};

// Custom hook for UI state
export const useUI = () => {
  const ui = useSelector((state) => state.ui);
  const dispatch = useDispatch();
  
  return { ui, dispatch };
};

// Custom hook for notifications
export const useNotifications = () => {
  const notifications = useSelector((state) => state.ui.notifications);
  const dispatch = useDispatch();
  
  const addNotification = useCallback((notification) => {
    dispatch({ type: 'ui/addNotification', payload: notification });
  }, [dispatch]);
  
  const removeNotification = useCallback((id) => {
    dispatch({ type: 'ui/removeNotification', payload: id });
  }, [dispatch]);
  
  const clearNotifications = useCallback(() => {
    dispatch({ type: 'ui/clearNotifications' });
  }, [dispatch]);
  
  return {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
  };
};

// Custom hook for loading states
export const useLoading = () => {
  const loading = useSelector((state) => state.ui.loading);
  const dispatch = useDispatch();
  
  const setLoading = useCallback((key, value) => {
    dispatch({ type: 'ui/setLoading', payload: { key, value } });
  }, [dispatch]);
  
  const setGlobalLoading = useCallback((value) => {
    dispatch({ type: 'ui/setGlobalLoading', payload: value });
  }, [dispatch]);
  
  return {
    loading,
    setLoading,
    setGlobalLoading,
  };
};

// Custom hook for modals
export const useModals = () => {
  const modals = useSelector((state) => state.ui.modals);
  const dispatch = useDispatch();
  
  const openModal = useCallback((modalName) => {
    dispatch({ type: 'ui/openModal', payload: modalName });
  }, [dispatch]);
  
  const closeModal = useCallback((modalName) => {
    dispatch({ type: 'ui/closeModal', payload: modalName });
  }, [dispatch]);
  
  const closeAllModals = useCallback(() => {
    dispatch({ type: 'ui/closeAllModals' });
  }, [dispatch]);
  
  return {
    modals,
    openModal,
    closeModal,
    closeAllModals,
  };
};

// Custom hook for filters
export const useFilters = () => {
  const filters = useSelector((state) => state.ui.filters);
  const dispatch = useDispatch();
  
  const setFilter = useCallback((type, filter, value) => {
    dispatch({ type: 'ui/setFilter', payload: { type, filter, value } });
  }, [dispatch]);
  
  const clearFilters = useCallback((type) => {
    dispatch({ type: 'ui/clearFilters', payload: type });
  }, [dispatch]);
  
  return {
    filters,
    setFilter,
    clearFilters,
  };
};

// Custom hook for pagination
export const usePagination = () => {
  const pagination = useSelector((state) => state.ui.pagination);
  const dispatch = useDispatch();
  
  const setPagination = useCallback((type, paginationData) => {
    dispatch({ type: 'ui/setPagination', payload: { type, pagination: paginationData } });
  }, [dispatch]);
  
  const resetPagination = useCallback((type) => {
    dispatch({ type: 'ui/resetPagination', payload: type });
  }, [dispatch]);
  
  return {
    pagination,
    setPagination,
    resetPagination,
  };
};
